import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerModule, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';

import { SharedConstants } from '@shared/constants';
import { FormatDatePipe } from '@shared/pipes/format-date.pipe';
import { CustomDateParserFormatterService } from '@shared/services/custom-date-parser-formatter.service';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-datepicker-range-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, NgbDatepickerModule],
    providers: [FormatDatePipe],
    styleUrl: './sas-datepicker-range-field-type.component.scss',
    template: `
        @if (field.props.label?.length){
        <label [for]="instanceId"
            [id]="field.id + 'label'"
            title="{{(field.props.label || '') | translate}}"
            [textContent]="field.props.label"
            translate="{{field.props.label}}"
            class="form-label form-date-label">
        </label>
        <div [title]="helpText" class="sas-inline-block">
            @if (!isReadonly) {
            <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
            <span [id]="field.id + 'Help'" class="visually-hidden">{{helpText}}</span>
            }
        </div>
        }
        <div class="row mb-3">
            <div class="col-12">
                <div class="dp-hidden position-absolute">
                    <div class="mb-3 input-group">
                        <input name="datepicker"
                               class="form-control"
                               [ariaLabel]="field.id + 'label'"
                               ngbDatepicker
                               #datepicker="ngbDatepicker"
                               [autoClose]="'outside'"
                               (dateSelect)="onDateSelection($event, datepicker)"
                               (closed)="sendRange()"
                               [displayMonths]="2"
                               [dayTemplate]="dateRangePickerTemplate"
                               outsideDays="hidden"
                               [startDate]="fromDate!"
                               [readOnly]="isReadonly"
                               tabindex="-1"
                               [footerTemplate]="footerTemplate"
                               [attr.aria-describedby]="field.id + 'Help'"/>
                        <ng-template #dateRangePickerTemplate let-date let-focused="focused">
                            <span class="custom-day"
                                  [class.focused]="focused"
                                  [class.range]="isRange(date)"
                                  [class.faded]="isHovered(date) || isInside(date)"
                                  (mouseenter)="hoveredDate = date"
                                  (mouseleave)="hoveredDate = null">
                                {{ date.day }}
                            </span>
                        </ng-template>
                        <ng-template #footerTemplate>
                            <hr class="my-0" />
                            <div class="btn-group btn-group-sm m-2 d-flex" role="group">
                                <button type="button"
                                        class="btn btn-outline-primary"
                                        (click)="shortcutSelected(rangePickerShortcuts.LAST_7_DAYS, datepicker)">
                                    Last 7 days
                                </button>
                                <button type="button"
                                        class="btn btn-outline-primary"
                                        (click)="shortcutSelected(rangePickerShortcuts.LAST_30_DAYS, datepicker)">
                                    Last 30 days
                                </button>
                                <button type="button"
                                        class="btn btn-outline-primary"
                                        (click)="shortcutSelected(rangePickerShortcuts.LAST_YEAR, datepicker)">
                                    Last year
                                </button>
                            </div>
                        </ng-template>
                    </div>
                </div>
                <div class="input-group">
                    <input #dpFromToDate
                           [id]="instanceId"
                           class="form-control form-control-datepicker"
                           [ngClass]="{'is-invalid' : formControl.invalid && (formControl.dirty || formSubmittedService.isSubmitted())}"
                           [placeholder]="isReadonly ? '' : (placeholder | translate)"
                           name="dpFromToDate"
                           [readOnly]="isReadonly"
                           [value]="formattedFromTo()"
                           (input)="onManualInput(dpFromToDate.value)"
                           [attr.aria-describedby]="field.id + 'Help'"/>
                    @if (!isReadonly) {
                    <button class="btn btn-outline-secondary bi bi-calendar3" (click)="datepicker.toggle()" type="button">
                        <span class="visually-hidden">Toggle date picker</span>
                    </button>
                    }
                </div>
            </div>
        </div>
    `
})
export class SasDatepickerRangeFieldTypeComponent extends FieldType<FieldTypeConfig> {
    private translate = inject(TranslateService);
    private destroyRef = inject(DestroyRef);
    formSubmittedService = inject(FormSubmittedService);
    formatter = inject(NgbDateParserFormatter);
    calendar = inject(NgbCalendar);
    customFormatter = inject(CustomDateParserFormatterService);
    formatDatePipe = inject(FormatDatePipe);

    instanceId: string = '';
    private random = Math.random().toString(36).substring(2);
    placeholder: string = 'Select date range';
    helpText!: string;
    rangePickerShortcuts = SharedConstants.DATE_RANGE_PICKER_SHORTCUTS;

    hoveredDate: NgbDate | null = null;
    fromDate: NgbDate | null = null;// = this.calendar.getToday();
    toDate: NgbDate | null = null;// = this.calendar.getNext(this.calendar.getToday(), 'd', 10);
    fromToDate: any; // to do : create an interface for this type
    formattedFromTo = signal('');
    private checkDatesTimeoutSub?: Subscription;

    constructor() {
        super()
    }

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    ngOnInit() {
        this.instanceId = this.field.id + this.random;

        // Sync UI when Formly resets the form control externally
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                if (!value?.from && !value?.to) {
                    this.fromDate = null;
                    this.toDate = null;
                    this.fromToDate = undefined;
                    this.formattedFromTo.set('');
                }
            });

        this.field.formControl.errors?.['required'];
        if (this.field.props !== undefined && this.field.props['placeholder'] !== undefined && this.field.props['placeholder'].length > 0) {
            this.placeholder = this.field.props['placeholder'];
        }

        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
            this.helpText = this.helpText.length > 0 ? `${this.helpText}. Format is dd/mm/yyyy - dd/mm/yyyy` : 'Format is dd/mm/yyyy - dd/mm/yyyy';
        }
        else {
            this.helpText = 'Format is dd/mm/yyyy - dd/mm/yyyy';
        }

        if (this.field.formControl.value !== undefined) {
            const dateStructFrom = this.customFormatter.parse(
                this.formatDatePipe.transform(
                    this.field.formControl.value.from,
                    SharedConstants.dateAPIFormat,
                    SharedConstants.dateOutputFormat
                )
            );
            const dateStructTo = this.customFormatter.parse(
                this.formatDatePipe.transform(
                    this.field.formControl.value.to,
                    SharedConstants.dateAPIFormat,
                    SharedConstants.dateOutputFormat
                )
            );
            this.onDateSelection(new NgbDate(dateStructFrom!.year, dateStructFrom!.month, dateStructFrom!.day));
            this.onDateSelection(new NgbDate(dateStructTo!.year, dateStructTo!.month, dateStructTo!.day));
            this.formatFromTo(this.fromDate, this.toDate);
        }
    }

    onDateSelection(date: NgbDate, datePicker?: NgbInputDatepicker) {
        if (!this.fromDate && !this.toDate) {
            this.fromDate = date;
        }
        else if (this.fromDate && !this.toDate && date && (date.equals(this.fromDate) || date.after(this.fromDate))) {
            this.toDate = date;
        }
        else {
            this.toDate = null;
            this.fromDate = date;
        }
        this.fromToDate = { from: this.fromDate, to: this.toDate };

        if (this.fromDate && this.toDate) {
            datePicker?.close();
        }

        this.formatFromTo(this.fromDate, this.toDate);
    }

    sendRange() {
        if (this.fromToDate !== undefined) {
            this.field.props['sasModel'] = {
                from: this.customFormatter.formatToOutput(this.fromToDate.from),
                to: this.customFormatter.formatToOutput(this.fromToDate.to)
            }
            this.formControl.setValue(this.field.props['sasModel']);
        }
    }

    onManualInput(value: string) {
        this.checkDatesTimeoutSub?.unsubscribe();

        if (!value || value.trim().length === 0) {
            // Field is empty — clear values and errors only if not required
            this.fromDate = null;
            this.toDate = null;
            this.fromToDate = undefined;

            this.formControl.setValue({});
            this.clearFormatError(); // remove format error if present
            return;
        }

        const [fromStr, toStr] = value.split(' - ');
        const from = this.validateInput(this.fromDate, this.customFormatter.formatToOutput(fromStr));
        const to = this.validateInput(this.toDate, this.customFormatter.formatToOutput(toStr));

        if (from || to) {
            let rangePicker = this.formControl.value;

            if (from) {
                this.fromDate = from;
                this.toDate = to;

                this.formControl.setValue({ ...rangePicker, from: this.customFormatter.formatToOutput(from) });
            }
            else {
                this.setInvalidFormatError();

                return;
            }

            rangePicker = this.formControl.value;

            this.checkDatesTimeoutSub = timer(500).subscribe(() => {
                if (from && to && (from.before(to) || from.equals(to))) {
                    this.formControl.setValue({ ...rangePicker, to: this.customFormatter.formatToOutput(to) });
                    this.clearFormatError();
                }
                else {
                    this.formControl.setValue({ ...rangePicker, to: undefined });
                    this.setInvalidFormatError();
                }
            });
        }
        else {
            this.fromDate = null;
            this.toDate = null;
            this.fromToDate = undefined;
            this.formControl.setValue({});
            this.setInvalidFormatError();
        }

        this.formatFromTo(this.fromDate, this.toDate, true);
    }

    formatFromTo(from: NgbDate | null, to: NgbDate | null, isManualInput: boolean = false) {
        const formatOutput = from ? `${this.customFormatter.format(from)}` : '';

        if (isManualInput) {
            this.checkDatesTimeoutSub = timer(500).subscribe(() => {
                this.updateFormattedFromTo(from, to, formatOutput);
            });
        }
        else {
            this.updateFormattedFromTo(from, to, formatOutput);
        }
    }

    private updateFormattedFromTo(from: NgbDate | null, to: NgbDate | null, formatOutput: string) {
        if (from && to) {
            this.formattedFromTo.set(
                from.before(to) || from.equals(to)
                    ? `${formatOutput} - ${this.customFormatter.format(to)}`
                    : `${formatOutput} - `
            );
        }
        else {
            this.formattedFromTo.set(formatOutput);
        }
    }

    isHovered(date: NgbDate) {
        return (this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate));
    }

    isInside(date: NgbDate) {
        return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
    }

    isRange(date: NgbDate) {
        return (
            date.equals(this.fromDate) ||
            (this.toDate && date.equals(this.toDate)) ||
            this.isInside(date) ||
            this.isHovered(date)
        );
    }

    private validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
        const parsed = this.formatter.parse(input);
        return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : null;
    }

    shortcutSelected(shortcut: string, datePicker?: NgbInputDatepicker) {
        const period: any = {};

        if (shortcut === this.rangePickerShortcuts.LAST_7_DAYS) {
            period.days = 6;
        }
        else if (shortcut === this.rangePickerShortcuts.LAST_30_DAYS) {
            period.days = 29;
        }
        else if (shortcut === this.rangePickerShortcuts.LAST_YEAR) {
            const today = this.calendar.getToday();
            const lastYear = today.year - 1;

            const daysInLastYear = this.isLeapYear(lastYear) ? 366 : 365;

            period.days = daysInLastYear - 1;
        }

        const { start, end } = this.getShortcutRange(period);

        this.fromDate = start;
        this.toDate = end;
        this.fromToDate = { from: this.fromDate, to: this.toDate };

        this.formatFromTo(this.fromDate, this.toDate);

        datePicker?.close();
    }

    private getShortcutRange({ days }: { days: number }): any {
        const end = this.calendar.getToday();
        const start = this.calendar.getPrev(end, 'd', days); // days before today

        return { start, end };
    }

    private isLeapYear(year: number): boolean {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    }

    private setInvalidFormatError() {
        this.formControl.setErrors({ ...this.formControl.errors, invalidFormat: true });
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
    }

    private clearFormatError() {
        if (this.formControl.hasError('invalidFormat')) {
            const errors = { ...this.formControl.errors };
            delete errors['invalidFormat'];
            this.formControl.setErrors(Object.keys(errors).length ? errors : null);
        }
    }


    ngOnDestroy() {
        this.checkDatesTimeoutSub?.unsubscribe();
    }
}
